import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Info, ExternalLink, Copy } from 'lucide-react';

export default function SetupGuideTab({ config, onCopy }) {
  const codeSnippet = `<!-- Google Publisher Tag in index.html -->
<script async src="https://securepubads.g.doubleclick.net/tag/js/gpt.js"></script>
<script>
  window.googletag = window.googletag || {cmd: []};
  googletag.cmd.push(function() {
    // Define ad slots
    googletag.defineSlot('/${config.network_code || 'YOUR_NETWORK_CODE'}/homepage_banner', 
      [[970, 90], [728, 90], [320, 50]], 'div-gpt-homepage')
      .addService(googletag.pubads());
    
    // Enable services
    googletag.pubads().enableSingleRequest();
    googletag.enableServices();
  });
</script>`;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Google Ad Manager Setup Guide</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <h4 className="font-medium">Step 1: Create a Google Ad Manager Account</h4>
            <p className="text-sm text-muted-foreground">
              Sign up at{' '}
              <a
                href="https://admanager.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                admanager.google.com
                <ExternalLink className="w-3 h-3 inline ml-1" />
              </a>
            </p>
          </div>

          <div className="space-y-4">
            <h4 className="font-medium">Step 2: Find Your Network Code</h4>
            <p className="text-sm text-muted-foreground">
              Go to Admin → Global Settings → Network Code. It&apos;s usually an 8-digit number.
            </p>
          </div>

          <div className="space-y-4">
            <h4 className="font-medium">Step 3: Create Ad Units</h4>
            <p className="text-sm text-muted-foreground">
              Create ad units for each placement:
            </p>
            <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
              <li>Homepage Banner (970x90, 728x90, 320x50)</li>
              <li>Sidebar Rectangle (300x250, 300x600)</li>
              <li>In-Feed (300x250, 336x280)</li>
              <li>Footer Banner (728x90, 320x50)</li>
            </ul>
          </div>

          <div className="space-y-4">
            <h4 className="font-medium">Step 4: Configure Line Items</h4>
            <p className="text-sm text-muted-foreground">
              Set up line items in GAM to serve ads to your ad units. You can use:
            </p>
            <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
              <li>AdSense backfill for programmatic ads</li>
              <li>Direct campaigns for sponsorship deals</li>
              <li>House ads for internal promotions</li>
            </ul>
          </div>

          <div className="space-y-4">
            <h4 className="font-medium">Step 5: Update Configuration</h4>
            <p className="text-sm text-muted-foreground">
              Enter your Network Code and Ad Unit paths in the Google Ad Manager tab above, then
              enable Google Ads.
            </p>
          </div>

          <Alert>
            <Info className="w-4 h-4" />
            <AlertDescription>
              <strong>Note:</strong> For production deployment, ad configuration should be moved
              to environment variables or a secure backend configuration system.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>GPT.js Integration Code</CardTitle>
          <CardDescription>
            Reference code for Google Publisher Tags integration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto">{codeSnippet}</pre>
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-2 right-2"
              onClick={() =>
                onCopy(
                  `<script async src="https://securepubads.g.doubleclick.net/tag/js/gpt.js"></script>`
                )
              }
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
